import React, { useEffect, useMemo, useState } from 'react';
import { AutoComplete, Form, Input, Select, Table, Tag } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useTable from '@/hooks/useTable';
import {
  getConsumerGroupChartData,
  getConsumerGroupList,
} from '@/services/kopConsumerGroupService';
import ShadowCard from '@/components/ShadowCard';
import { renderDateTime, renderNum, renderNumRate, sleep, toTsOptions } from '@/utils/common';
import { fetchKopNsOptions } from '@/slices/utilSlice';
import { getAllTopics } from '@/services/utilService';
import PopupLink from '@/components/Cluster/PopupLink';
import SvgIcon from '@/components/SvgIcon';
import { isKafkaCluster } from '@/pages/Layout/MenuLayout/CulsterAddon';
import Dashboard from './Dashboard';
import Detail from './Detail';

const { FormItem } = Form;

function Body({ clusterName, fetchKopNsOptions, nsOptions, isKafka }) {
  const [topicOptions, setTopicOptions] = useState([]);
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [searchParams] = useSearchParams();
  const [currentRow, setCurrentRow] = useState({});

  // 查询初始化
  const initialData = useMemo(
    () => ({
      namespace: searchParams.get('namespace'),
      topic: searchParams.get('topic'),
    }),
    [searchParams],
  );

  const columns = [
    {
      colKey: 'consumerGroupName',
      title: 'ConsumerGroup',
      width: 200,
      cell: ({ row }) => (
        <Detail consumerGroupName={row.consumerGroupName} clusterName={clusterName} />
      ),
      fixed: 'left',
    },
    {
      colKey: 'consumedTopicName',
      title: '消费的Topic',
      cell: ({ row }) =>
        row.consumedTopicName?.split(',').map((item, index) => (
          <div key={index}>
            <Tag theme='default' style={{ margin: '2px 0' }}>
              <PopupLink
                style={{ display: 'block' }}
                tip='跳转Topic'
                onClick={() => {
                  navigate(`/kop-cluster/topic/list?name=${clusterName}&topic=${item}`);
                }}
              >
                {item}
              </PopupLink>
            </Tag>
          </div>
        )),
    },
    {
      colKey: 'status',
      title: 'Status',
      sorter: true,
      defaultSort: 'asc',
      width: 100,
    },
    {
      colKey: 'totalBytesOut',
      title: '消费速率',
      cell: renderNumRate,
      width: 110,
    },
    {
      colKey: 'memberSize',
      title: 'Member数',
      cell: renderNum,
      width: 110,
    },
    {
      colKey: 'totalLag',
      title: '总积压',
      cell: renderNum,
      width: 120,
    },
    {
      colKey: 'updateTime',
      title: '更新时间',
      cell: renderDateTime,
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Icon
          type='presentation-chart-line'
          onClick={() => {
            setVisible(true);
            setCurrentRow(row);
          }}
        />
      ),
      width: 60,
    },
  ];

  const namespaceChange = useMemoizedFn(async (value) => {
    form.setFieldsValue({ topic: undefined });
    setTopicOptions([]);
    if (value) {
      const resp = await getAllTopics({
        clusterName,
        namespace: value,
      });
      setTopicOptions(resp.data);
    }
  });

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getConsumerGroupList({
      clusterName,
      ...params,
    });
    setData(data);
  });

  const { config, header, setData, doSearch, form } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <>
        <FormItem name='consumerGroupName'>
          <Input placeholder='请输入ConsumerGroup' />
        </FormItem>
        {!isKafka && (
          <FormItem name='namespace'>
            <Select
              onChange={namespaceChange}
              options={toTsOptions(nsOptions)}
              placeholder='选择命名空间'
              clearable
            />
          </FormItem>
        )}
        <FormItem name='topic'>
          <AutoComplete
            style={{ width: '100%' }}
            popupProps={{ overlayInnerStyle: { width: 'auto' } }}
            options={topicOptions}
            placeholder='请输入Topic'
            clearable
          />
        </FormItem>
      </>
    ),
  });

  const doInit = useMemoizedFn(async () => {
    form.reset();
    await sleep(30);
    // 有初始化条件
    if (searchParams.size > 1) {
      await doSearch(initialData);
      form.setFieldsValue(initialData);
    } else {
      doSearch();
    }
  });

  useEffect(() => {
    doInit();
  }, [doInit, clusterName]);

  useEffect(() => {
    if (clusterName) {
      fetchKopNsOptions(clusterName);
    }
  }, [fetchKopNsOptions, clusterName]);

  const handleClose = () => {
    setVisible(false);
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        getChartsData={(params) =>
          getConsumerGroupChartData({
            clusterName,
            consumerGroup: currentRow.consumerGroupName,
            ...params,
          })
        }
        handleClose={handleClose}
      />
    </Root>
  );
}

export default connect(
  (state) => ({
    nsOptions: state.util.kopNsOptions,
    isKafka: isKafkaCluster(state.kopCluster.allList), // 判断是不是kafka节点集群
  }),
  {
    fetchKopNsOptions,
  },
)(Body);

const Root = styled(ShadowCard)`
  flex: 1;
  margin-top: 12px;
  .is-deleted {
    background-color: var(--td-error-color-1);
  }
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
